/**
 * CR071: Merchant Logo Cell - Displays logo with edit capability
 */

import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Link, Pencil } from "lucide-react";
import { resolveMerchantLogo, getLogoFallbackInitial } from "@/lib/merchantLogoResolver";
import type { Merchant } from "@/hooks/useMerchants";
import { cn } from "@/lib/utils";

interface MerchantLogoCellProps {
  merchantKey: string;
  merchantName: string;
  merchant?: Merchant | null;
  onUpdateLogo?: (merchantKey: string, data: { logo_url?: string | null; logo_source?: string }) => Promise<boolean>;
  onUploadLogo?: (merchantKey: string, file: File) => Promise<boolean>;
  size?: "sm" | "md" | "lg";
  editable?: boolean;
}

export function MerchantLogoCell({
  merchantKey,
  merchantName,
  merchant,
  onUpdateLogo,
  onUploadLogo,
  size = "sm",
  editable = true,
}: MerchantLogoCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get logo from resolver (which checks DB first, then legacy)
  const logoResult = resolveMerchantLogo(merchantName);
  const fallbackInitial = getLogoFallbackInitial(merchantName);

  const sizeClasses = {
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12",
  };

  const handleUrlSave = async () => {
    if (!onUpdateLogo || !urlInput.trim()) return;
    setIsLoading(true);
    const success = await onUpdateLogo(merchantKey, {
      logo_url: urlInput.trim(),
      logo_source: "url",
    });
    setIsLoading(false);
    if (success) {
      setIsEditing(false);
      setUrlInput("");
      setLogoError(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadLogo) return;
    
    setIsLoading(true);
    const success = await onUploadLogo(merchantKey, file);
    setIsLoading(false);
    if (success) {
      setIsEditing(false);
      setLogoError(false);
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const renderLogo = () => {
    // CR201: Use img for all logo types - never dangerouslySetInnerHTML
    if ((logoResult.svg || logoResult.src) && !logoError) {
      return (
        <img
          src={logoResult.svg 
            ? `data:image/svg+xml,${encodeURIComponent(logoResult.svg)}`
            : logoResult.src!
          }
          alt={merchantName}
          className={cn(sizeClasses[size], "object-contain rounded")}
          onError={() => setLogoError(true)}
        />
      );
    }

    // Fallback: show initial
    return (
      <div
        className={cn(
          sizeClasses[size],
          "rounded bg-muted flex items-center justify-center text-muted-foreground font-semibold"
        )}
      >
        {fallbackInitial}
      </div>
    );
  };

  if (!editable) {
    return <div className="flex items-center">{renderLogo()}</div>;
  }

  return (
    <Popover open={isEditing} onOpenChange={setIsEditing}>
      <PopoverTrigger asChild>
        <button
          className="group relative flex items-center cursor-pointer hover:opacity-80 transition-opacity"
          title="Click to edit logo"
        >
          {renderLogo()}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
            <Pencil className="h-3 w-3 text-white" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <div className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Edit Logo</h4>
            <p className="text-xs text-muted-foreground">
              Update logo for <strong>{merchantName}</strong>
            </p>
          </div>

          <Tabs defaultValue="url">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="url" className="text-xs">
                <Link className="h-3 w-3 mr-1" />
                Paste URL
              </TabsTrigger>
              <TabsTrigger value="upload" className="text-xs">
                <Upload className="h-3 w-3 mr-1" />
                Upload
              </TabsTrigger>
            </TabsList>

            <TabsContent value="url" className="space-y-3 mt-3">
              <div className="space-y-2">
                <Label htmlFor="logo-url" className="text-xs">
                  Logo URL
                </Label>
                <Input
                  id="logo-url"
                  placeholder="https://example.com/logo.svg"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleUrlSave}
                  disabled={!urlInput.trim() || isLoading}
                  className="flex-1"
                >
                  {isLoading ? "Saving..." : "Save"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="upload" className="space-y-3 mt-3">
              <div className="space-y-2">
                <Label className="text-xs">Upload file</Label>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/svg+xml,image/png,image/jpeg,image/webp"
                    onChange={handleFileUpload}
                    className="hidden"
                    id={`logo-upload-${merchantKey}`}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                    className="w-full"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {isLoading ? "Uploading..." : "Choose file"}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  SVG, PNG, JPG, or WebP (max 5MB)
                </p>
              </div>
            </TabsContent>
          </Tabs>

          {/* Current source info */}
          {merchant?.logo_source && (
            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                Current source: <span className="font-medium">{merchant.logo_source}</span>
              </p>
              {merchant.logo_updated_at && (
                <p className="text-xs text-muted-foreground">
                  Updated: {new Date(merchant.logo_updated_at).toLocaleDateString()}
                </p>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
